/**
 * Language toggle widget — a small, accessible "EN / ES" segmented control.
 *
 * Kept framework-free to match the rest of the app: `renderLanguageToggle()`
 * returns an HTML string embedded into a header template, and
 * `setupLanguageToggle()` wires the click handlers after that HTML is mounted.
 * Selecting a locale flips the shared i18n context, which triggers a re-render
 * of the active view — no page reload.
 */

import { getLocale, setLocale, SUPPORTED_LOCALES, t, type Locale } from "./config";

/** Short, locale-agnostic labels for each option. */
const LOCALE_LABELS: Record<Locale, string> = {
  en: "EN",
  es: "ES",
};

/**
 * Render the toggle. Styling leans on the existing design tokens so it reads
 * correctly across the light, dark, and daylight skins. The active option is
 * highlighted and exposed to assistive tech via `aria-pressed`.
 */
export function renderLanguageToggle(): string {
  const current = getLocale();

  const options = SUPPORTED_LOCALES.map((locale) => {
    const active = locale === current;
    const activeClasses = active
      ? "bg-primary text-white daylight:bg-day-ink daylight:text-white shadow-sm"
      : "text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft hover:text-primary daylight:hover:text-day-ink";
    return `
          <button
            type="button"
            data-locale="${locale}"
            aria-pressed="${active}"
            class="lang-option px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide transition-colors ${activeClasses}"
          >${LOCALE_LABELS[locale]}</button>`;
  }).join("");

  return `
      <div
        class="lang-toggle inline-flex items-center gap-1 rounded-lg border border-slate-300 dark:border-slate-700 daylight:border-day-ink/20 p-0.5"
        role="group"
        aria-label="${t("language.label")}"
      >${options}</div>`;
}

/**
 * Attach click handlers to a rendered toggle within `container`. Idempotent per
 * mount: call it once after inserting the header HTML.
 */
export function setupLanguageToggle(container: HTMLElement): void {
  const buttons = container.querySelectorAll<HTMLButtonElement>(".lang-toggle [data-locale]");
  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      const locale = button.dataset.locale as Locale | undefined;
      if (locale) setLocale(locale);
    });
  });
}
